import { Alert, Button, Spin } from "@arco-design/web-react";
import { ArrowLeft, ListChecks, Pencil, RefreshCw } from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { isHttpClientError } from "../../../lib/http/errors";
import { useQuoteBudgetEntryRedirect } from "..";
import "./QuoteBudgetEntryRedirectPage.css";

/** 读取错误对象上的后端业务码. */
function readErrorCode(error: unknown): string | null {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/** 行程进入核价预算入口页：调用 enter 后 replace 到编辑页. */
export function QuoteBudgetEntryRedirectPage() {
  const { itineraryId } = useParams<{ itineraryId: string }>();
  const navigate = useNavigate();
  const query = useQuoteBudgetEntryRedirect(itineraryId);

  if (query.data?.status === "requires_repair") {
    return (
      <main className="quote-budget-entry-state">
        <div className="quote-budget-entry-state-inner">
          <Alert
            type="warning"
            showIcon
            content="行程数据不完整，请打开行程编辑页面手动修复后再核价。"
          />
          <div className="quote-budget-entry-state-actions">
            <Button
              icon={<ArrowLeft size={16} />}
              onClick={() => navigate("/product-lines/course-routes", { replace: true })}
            >
              返回课程线路
            </Button>
            <Button
              type="primary"
              icon={<Pencil size={16} />}
              onClick={() => navigate(`/itineraries/${itineraryId}/edit`, { replace: true })}
            >
              打开行程编辑
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (query.data) {
    return <Navigate to={query.data.redirectTo} replace />;
  }

  if (query.error) {
    const errorView = resolveEntryError(query.error);
    return (
      <main className="quote-budget-entry-state">
        <div className="quote-budget-entry-state-inner">
          <Alert type={errorView.type} showIcon content={errorView.message} />
          <div className="quote-budget-entry-state-actions">
            <Button icon={<ArrowLeft size={16} />} onClick={() => navigate("/product-lines/course-routes", { replace: true })}>
              返回课程线路
            </Button>
            {errorView.canRetry ? (
              <Button
                type="primary"
                icon={<RefreshCw size={16} />}
                loading={query.isFetching}
                onClick={() => void query.refetch()}
              >
                重新尝试
              </Button>
            ) : null}
            {errorView.action ? (
              <Button
                type="primary"
                icon={<ListChecks size={16} />}
                onClick={() => navigate(errorView.action!.path, { replace: true })}
              >
                {errorView.action.label}
              </Button>
            ) : null}
          </div>
        </div>
      </main>
    );
  }

  return <EntryLoading text="正在进入行程核价…" />;
}

/** 统一入口等待状态，避免导航期间出现无语义的裸文本。 */
function EntryLoading({ text }: { text: string }) {
  return <main className="quote-budget-entry-state" role="status"><Spin size={32} tip={text} /></main>;
}

/** 将核价入口异常映射为稳定且可恢复的用户提示。 */
function resolveEntryError(error: unknown): {
  message: string;
  type: "error" | "warning";
  canRetry: boolean;
  action?: { label: string; path: string };
} {
  const code = readErrorCode(error);
  const businessBlockMessages: Record<string, string> = {
    CUSTOMIZATION_CURRENT_DEMAND_INCOMPLETE: "当前行程关联的定制需求尚未补全，请先补齐必填项后再进入核价。",
    CUSTOMIZATION_OPPORTUNITY_DISABLED: "当前商机已停用，不能继续核价；请先启用商机后重试。",
    CUSTOMIZATION_OPPORTUNITY_BINDING_REQUIRED: "当前行程关联的定制尚未完成商机归属，请先前往定制列表完成归属后再进入核价。",
    CUSTOMIZATION_OPPORTUNITY_REVIEW_REQUIRED: "当前行程关联的商机归属需要人工复核，请先前往定制列表完成复核后再进入核价。",
    CUSTOMIZATION_OPPORTUNITY_CONFLICT: "当前行程关联的商机归属存在冲突，请先前往定制列表处理归属冲突后再进入核价。",
  };
  if (code && businessBlockMessages[code]) {
    return {
      message: businessBlockMessages[code],
      type: "warning",
      canRetry: false,
      action: { label: "前往定制列表", path: "/crm/customization/projects" },
    };
  }
  if (code === "QUOTE_BUDGET_SCHEMA_NOT_READY") {
    return { message: "行程核价数据表尚未就绪，请稍后再试。", type: "warning", canRetry: true };
  }
  if (isHttpClientError(error)) {
    if (error.statusCode === 404 || code === "ITINERARY_NOT_FOUND" || code === "QUOTE_BUDGET_NOT_FOUND") {
      return { message: "未找到该行程，可能已删除或链接无效。", type: "error", canRetry: false };
    }
    if (error.statusCode === 403) {
      return { message: "当前账号没有进入行程核价的权限。", type: "error", canRetry: false };
    }
  }
  return { message: "暂时无法进入行程核价，请检查网络后重试。", type: "error", canRetry: true };
}
